import { faker } from "@faker-js/faker";

/**
 * Maps lowercase field name patterns to generator functions.
 *
 * When the mock schema generator encounters a ZodString, it looks up the field
 * name here (exact match first, then suffix match). Add entries here when a
 * field name produces semantically wrong values (e.g. "currency" must be a
 * valid ISO 4217 code, not a lorem word).
 *
 * Order matters — first match wins.
 */
export const fieldGenerators: {
  match: (fieldName: string) => boolean;
  generate: () => string;
}[] = [
  { match: (n) => n === "currency", generate: () => "USD" },
  {
    match: (n) => n === "country" || n === "countrycode",
    generate: () => "US",
  },
  { match: (n) => n === "locale", generate: () => "en-US" },
  { match: (n) => n === "timezone", generate: () => "America/New_York" },
  { match: (n) => n === "color", generate: () => faker.color.human() },
  { match: (n) => n === "email", generate: () => faker.internet.email() },
  { match: (n) => n.includes("phone"), generate: () => faker.phone.number() },
  {
    match: (n) =>
      n === "image" ||
      n === "imageurl" ||
      n === "imagesrc" ||
      n.endsWith("image") ||
      n.endsWith("imageurl") ||
      n.endsWith("imagesrc") ||
      n.endsWith("thumbnail") ||
      n.endsWith("avatar"),
    generate: () =>
      `https://picsum.photos/seed/${faker.string.alphanumeric(8)}/400/400`,
  },
  {
    match: (n) => n === "url" || n.endsWith("url"),
    generate: () => faker.internet.url(),
  },
  { match: (n) => n === "firstname", generate: () => faker.person.firstName() },
  { match: (n) => n === "lastname", generate: () => faker.person.lastName() },
  {
    match: (n) => n === "name" || n.endsWith("name"),
    generate: () => faker.person.fullName(),
  },
  { match: (n) => n === "title", generate: () => faker.lorem.words(3) },
  {
    match: (n) => n === "description" || n === "message" || n === "body",
    generate: () => faker.lorem.sentence(),
  },
  {
    match: (n) =>
      n === "id" || n === "_id" || (n.endsWith("id") && n.length > 2),
    generate: () => faker.database.mongodbObjectId(),
  },
  {
    match: (n) =>
      n === "date" ||
      n === "createdat" ||
      n === "updatedat" ||
      n === "timestamp" ||
      n === "time" ||
      n.endsWith("date") ||
      n.endsWith("at") ||
      n.endsWith("time") ||
      n.endsWith("timestamp"),
    generate: () => new Date().toISOString(),
  },
];

export function generateStringForField(fieldName?: string): string {
  if (fieldName) {
    const lower = fieldName.toLowerCase();
    for (const entry of fieldGenerators) {
      if (entry.match(lower)) {
        return entry.generate();
      }
    }
  }
  return faker.lorem.word();
}
